'use client';

import { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Home, RefreshCw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { locales, defaultLocale } from '@/i18n';
import { getLocalizedHome } from '@/lib/locale-utils';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations('error');
  // Extract locale from pathname (e.g., /en/page or /es/page)
  const pathSegments = pathname?.split('/').filter(Boolean) || [];
  const locale = pathSegments[0] && locales.includes(pathSegments[0] as any)
    ? pathSegments[0]
    : defaultLocale;
  const homePath = getLocalizedHome(locale);

  useEffect(() => {
    // Log the error to an error reporting service

  }, [error]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-black dark:to-gray-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative z-10 text-center max-w-4xl mx-auto">
        {/* Error Icon */}

        {/* Error Code */}
        <h1 className="text-8xl md:text-9xl font-bold bg-gradient-to-r from-primary-600 via-blue-600 to-primary-600 bg-clip-text text-transparent mb-4 tracking-tight">
          500
        </h1>

        {/* Error Title */}
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          {t('title') || 'Something went wrong!'}
        </h2>

        {/* Error Message */}
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-10 max-w-md mx-auto">
          {error.message || t('description') || 'An unexpected error occurred. Please try again or return to the homepage.'}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 px-10 py-4 text-lg font-semibold rounded-lg border-2 border-primary-600 dark:border-primary-500 bg-transparent text-primary-600 dark:text-primary-400 hover:bg-primary-700 dark:hover:bg-primary-700 hover:text-white dark:hover:text-white hover:border-primary-700 dark:hover:border-primary-700 hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300"
          >
            <RefreshCw className="w-5 h-5" />
            {t('tryAgain') || 'Try again'}
          </button>
          <button
            onClick={() => router.push(homePath)}
            className="inline-flex items-center gap-2 px-10 py-4 text-lg font-semibold rounded-lg border-2 border-primary-600 dark:border-primary-500 bg-transparent text-primary-600 dark:text-primary-400 hover:bg-primary-700 dark:hover:bg-primary-700 hover:text-white dark:hover:text-white hover:border-primary-700 dark:hover:border-primary-700 hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300"
          >
            <Home className="w-5 h-5" />
            {t('goHome') || 'Go back home'}
          </button>

        </div>

        {/* Additional Help Text */}
        <p className="mt-10 text-sm text-gray-500 dark:text-gray-500">
          {t('helpText') || 'If this problem persists, please contact our support team.'}
        </p>
      </div>
    </div>
  );
}

